<script>
import { mapState } from "vuex";
import moment from "moment";

import Layout from "../../layouts/main";
import Stat from "@/components/widgets/stat";
import appConfig from "@/app.config";
import { etiquetaTipo } from "@/services/usuarios";
import { PRECIO_LISTA_MXN, DIAS_POR_VENCER } from "@/services/suscripciones";
import { porcentaje } from "@/services/metricas";
import { descargarCsv } from "@/services/csv";

const ESTADOS = [
  { value: "todos", text: "Todos los estados" },
  { value: "activo", text: "Activos" },
  { value: "por_vencer", text: "Por vencer" },
  { value: "cancelacion_programada", text: "Cancelación programada" },
  { value: "cancelado", text: "Cancelados" },
  { value: "sin_membresia", text: "Sin membresía" },
];

export default {
  page: {
    title: "Suscripciones",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: { Layout, Stat },
  data() {
    return {
      estados: ESTADOS,
      filtroEstado: "todos",
      filtroTipo: "todos",
      filtroCiudad: "todas",
      busqueda: "",
      currentPage: 1,
      perPage: 15,
      usuarioSeleccionado: null,
      mostrarModal: false,
      diasPorVencer: DIAS_POR_VENCER,
      precioLista: PRECIO_LISTA_MXN,
    };
  },
  computed: {
    ...mapState("analytics", ["cargando", "error"]),

    /**
     * Todo el padrón con su membresía resuelta. A diferencia de "Recnet Plus",
     * que sólo lista a quienes ya pagan, aquí están también los que no: sin
     * ellos no se puede medir conversión.
     */
    filas() {
      return this.$store.getters["analytics/usuariosConMembresia"].map((u) => ({
        id: u.id,
        name: u.name,
        email: u.email,
        phone: u.phone,
        tipo: u.tipo,
        tipoTexto: etiquetaTipo(u.tipo),
        ciudad: u.ciudad,
        creadoEn: u.creadoEn,
        creadoEnFormatted: u.creadoEnFormatted,
        referral_code: u.referral_code,
        used_referral_code: u.used_referral_code,
        estado: u.membresia.estado,
        estadoTexto: u.membresia.etiqueta,
        variante: u.membresia.variante,
        activo: u.membresia.activo,
        vigencia: u.membresia.vigencia,
        diasRestantes: u.membresia.diasRestantes,
      }));
    },

    ciudades() {
      const set = new Set(this.filas.map((f) => f.ciudad).filter(Boolean));
      return [
        { value: "todas", text: "Todas las ciudades" },
        ...Array.from(set).sort().map((c) => ({ value: c, text: c })),
      ];
    },

    filasFiltradas() {
      const q = this.busqueda.trim().toLowerCase();
      return this.filas.filter((f) => {
        if (this.filtroEstado !== "todos" && f.estado !== this.filtroEstado) return false;
        if (this.filtroTipo !== "todos" && f.tipo !== this.filtroTipo) return false;
        if (this.filtroCiudad !== "todas" && f.ciudad !== this.filtroCiudad) return false;
        if (!q) return true;
        return [f.name, f.email, f.phone, f.referral_code]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
      });
    },

    resumen() {
      const total = this.filas.length;
      const activos = this.filas.filter((f) => f.activo).length;
      const porVencer = this.filas.filter((f) => f.estado === "por_vencer").length;
      const programadas = this.filas.filter((f) => f.estado === "cancelacion_programada").length;
      return {
        total,
        activos,
        inactivos: total - activos,
        conversion: porcentaje(activos, total),
        porVencer,
        programadas,
        mrr: activos * PRECIO_LISTA_MXN,
      };
    },

    /** Referidos del usuario abierto en el modal, separados por membresía. */
    referidosDelSeleccionado() {
      if (!this.usuarioSeleccionado) return { conPlus: [], sinPlus: [] };
      const codigo = (this.usuarioSeleccionado.referral_code || "").toLowerCase();
      if (!codigo) return { conPlus: [], sinPlus: [] };

      const referidos = this.$store.getters["analytics/indiceReferidos"][codigo] || [];
      const filasPorEmail = new Map(this.filas.map((f) => [f.email, f]));
      const enriquecidos = referidos.map((r) => filasPorEmail.get(r.email) || r);

      return {
        conPlus: enriquecidos.filter((r) => r.activo),
        sinPlus: enriquecidos.filter((r) => !r.activo),
      };
    },

    referidorDelSeleccionado() {
      if (!this.usuarioSeleccionado || !this.usuarioSeleccionado.used_referral_code) return null;
      const codigo = this.usuarioSeleccionado.used_referral_code;
      return this.filas.find((f) => (f.referral_code || "").toLowerCase() === codigo) || null;
    },
  },
  watch: {
    filasFiltradas() {
      this.currentPage = 1;
    },
  },
  methods: {
    verDetalle(fila) {
      this.usuarioSeleccionado = fila;
      this.mostrarModal = true;
    },

    refrescar() {
      return this.$store.dispatch("analytics/refrescar");
    },

    exportar() {
      descargarCsv(
        "suscripciones",
        [
          { key: "name", label: "Nombre" },
          { key: "email", label: "Correo" },
          { key: "phone", label: "Teléfono" },
          { key: "tipoTexto", label: "Tipo" },
          { key: "ciudad", label: "Ciudad" },
          { key: "creadoEnFormatted", label: "Fecha de registro" },
          { key: "estadoTexto", label: "Estado" },
          { key: "vigencia", label: "Vigencia" },
          { key: "referral_code", label: "Código de referido" },
          { key: "used_referral_code", label: "Referido por" },
        ],
        this.filasFiltradas
      );
    },

    formatoMoneda(valor) {
      return new Intl.NumberFormat("es-MX", {
        style: "currency",
        currency: "MXN",
        maximumFractionDigits: 0,
      }).format(valor || 0);
    },

    fechaLarga(m) {
      return m && m.isValid() ? m.format("DD [de] MMMM [de] YYYY") : "—";
    },
  },
  mounted() {
    this.$store.dispatch("analytics/cargar");
  },
};
</script>

<template>
  <Layout>
    <div class="mb-4">
      <img :src="require('@/assets/images/recnet-header.png')" alt="Suscripciones"
        class="img-fluid w-100 rounded shadow-sm"
        style="max-height: 260px; object-fit: cover; object-position: center;" />
    </div>

    <b-row>
      <b-col md="6" xl="3">
        <Stat title="Suscriptores activos" :value="resumen.activos"
          :hint="`${resumen.conversion}% del padrón`" icon="mdi mdi-star-circle" variant="success" />
      </b-col>
      <b-col md="6" xl="3">
        <Stat title="Sin membresía" :value="resumen.inactivos" hint="Oportunidad de conversión"
          icon="mdi mdi-account-off" variant="secondary" />
      </b-col>
      <b-col md="6" xl="3">
        <Stat title="Por vencer" :value="resumen.porVencer"
          :hint="`Próximos ${diasPorVencer} días · ${resumen.programadas} con cancelación programada`"
          icon="mdi mdi-clock-alert" variant="warning" />
      </b-col>
      <b-col md="6" xl="3">
        <Stat title="Ingreso mensual estimado" :value="formatoMoneda(resumen.mrr)"
          :hint="`Activos × ${formatoMoneda(precioLista)} de lista`" icon="mdi mdi-cash-multiple" variant="info" />
      </b-col>
    </b-row>

    <div class="card">
      <div class="card-body">
        <b-alert :show="!!error" variant="warning">{{ error }}</b-alert>

        <b-row class="mb-3">
          <b-col md="4" class="mb-2">
            <b-form-input v-model="busqueda" type="search" placeholder="Nombre, correo, teléfono o código…" />
          </b-col>
          <b-col md="2" class="mb-2">
            <b-form-select v-model="filtroEstado" :options="estados" />
          </b-col>
          <b-col md="2" class="mb-2">
            <b-form-select v-model="filtroTipo" :options="[
              { value: 'todos', text: 'Todos los tipos' },
              { value: 'usuario', text: 'Usuarios' },
              { value: 'comercio', text: 'Comercios' },
              { value: 'sin_definir', text: 'Sin definir' },
            ]" />
          </b-col>
          <b-col md="2" class="mb-2">
            <b-form-select v-model="filtroCiudad" :options="ciudades" />
          </b-col>
          <b-col md="2" class="mb-2 text-md-right">
            <b-button variant="outline-secondary" size="sm" class="mr-1" :disabled="cargando" @click="refrescar">
              <i class="mdi mdi-refresh"></i>
            </b-button>
            <b-button variant="info" size="sm" @click="exportar">
              <i class="mdi mdi-download"></i> CSV
            </b-button>
          </b-col>
        </b-row>

        <p class="text-muted small">
          {{ filasFiltradas.length }} de {{ resumen.total }} usuarios
        </p>

        <b-table responsive hover :items="filasFiltradas" :per-page="perPage" :current-page="currentPage" :fields="[
          { key: 'name', label: 'Nombre', sortable: true },
          { key: 'phone', label: 'Teléfono' },
          { key: 'tipoTexto', label: 'Tipo', sortable: true },
          { key: 'ciudad', label: 'Ciudad', sortable: true },
          { key: 'creadoEnFormatted', label: 'Registro' },
          { key: 'estadoTexto', label: 'Estado', sortable: true },
          { key: 'vigencia', label: 'Vigencia' },
          { key: 'acciones', label: '' },
        ]" show-empty :empty-text="cargando ? 'Cargando…' : 'Sin resultados con estos filtros.'">
          <template #cell(name)="{ item }">
            {{ item.name }}<br />
            <small class="text-muted">{{ item.email }}</small>
          </template>
          <template #cell(estadoTexto)="{ item }">
            <b-badge :variant="item.variante">{{ item.estadoTexto }}</b-badge>
          </template>
          <template #cell(acciones)="{ item }">
            <b-button size="sm" variant="primary" @click="verDetalle(item)">
              <i class="mdi mdi-eye"></i>
            </b-button>
          </template>
        </b-table>

        <b-pagination v-model="currentPage" :total-rows="filasFiltradas.length" :per-page="perPage" align="right"
          size="sm" class="mt-3" first-number last-number />
      </div>
    </div>

    <b-modal v-model="mostrarModal" size="xl" centered hide-footer
      :title="usuarioSeleccionado ? usuarioSeleccionado.name : ''">
      <div v-if="usuarioSeleccionado">
        <b-badge :variant="usuarioSeleccionado.variante" class="mb-3">{{ usuarioSeleccionado.estadoTexto }}</b-badge>

        <b-row class="mb-3">
          <b-col md="6">
            <p class="mb-1"><strong>Correo:</strong> {{ usuarioSeleccionado.email || '—' }}</p>
            <p class="mb-1"><strong>Teléfono:</strong> {{ usuarioSeleccionado.phone || '—' }}</p>
            <p class="mb-1"><strong>Tipo:</strong> {{ usuarioSeleccionado.tipoTexto }}</p>
            <p class="mb-1"><strong>Ciudad:</strong> {{ usuarioSeleccionado.ciudad }}</p>
          </b-col>
          <b-col md="6">
            <p class="mb-1"><strong>Registro:</strong> {{ fechaLarga(usuarioSeleccionado.creadoEn) }}</p>
            <p class="mb-1"><strong>Vigencia:</strong> {{ usuarioSeleccionado.vigencia }}</p>
            <p class="mb-1"><strong>Código de referido:</strong> {{ usuarioSeleccionado.referral_code || '—' }}</p>
            <p class="mb-1">
              <strong>Referido por:</strong>
              <span v-if="referidorDelSeleccionado">
                {{ referidorDelSeleccionado.name }} ({{ referidorDelSeleccionado.email }})
              </span>
              <span v-else-if="usuarioSeleccionado.used_referral_code" class="text-muted">
                Código {{ usuarioSeleccionado.used_referral_code }} sin dueño registrado
              </span>
              <span v-else>—</span>
            </p>
          </b-col>
        </b-row>

        <h5 class="mt-3 mb-2 text-success">
          Referidos con membresía ({{ referidosDelSeleccionado.conPlus.length }})
        </h5>
        <b-table small responsive hover :items="referidosDelSeleccionado.conPlus" :fields="[
          { key: 'name', label: 'Nombre' },
          { key: 'email', label: 'Correo' },
          { key: 'phone', label: 'Teléfono' },
          { key: 'creadoEnFormatted', label: 'Registro' },
          { key: 'vigencia', label: 'Vigencia' },
        ]" show-empty empty-text="Sin referidos con membresía." class="mb-4" />

        <h5 class="mt-3 mb-2 text-danger">
          Referidos sin membresía ({{ referidosDelSeleccionado.sinPlus.length }})
        </h5>
        <b-table small responsive hover :items="referidosDelSeleccionado.sinPlus" :fields="[
          { key: 'name', label: 'Nombre' },
          { key: 'email', label: 'Correo' },
          { key: 'phone', label: 'Teléfono' },
          { key: 'creadoEnFormatted', label: 'Registro' },
        ]" show-empty empty-text="Sin referidos pendientes." />
      </div>

      <hr />
      <div class="d-flex justify-content-end">
        <b-button variant="secondary" @click="mostrarModal = false">Cerrar</b-button>
      </div>
    </b-modal>
  </Layout>
</template>
